// utils/offlineUtils.js
import { downloadEvents, DOWNLOAD_EVENTS } from 'db/offlineDownloadService';
import { toast } from 'react-toastify';
import { globalEventBus, GLOBAL_EVENTS } from './eventEmitter';
import offlineDBService from 'db/dexieDB';
import offlineDownloadService from 'db/offlineDownloadService';

/**
 * Handle downloading offline data with proper event tracking
 * @param {Object} options - Download options
 * @param {string} options.tenant - Tenant ID
 * @param {Function} options.setIsDownloading - Function to set downloading state
 * @param {Function} options.setDownloadSuccess - Function to set download success state
 * @returns {Promise<Object>} - Download result
 */
export const handleOfflineDownload = async ({
  tenant,
  setIsDownloading,
  setDownloadSuccess
}) => {
  const isOnline = navigator.onLine;

  if (!isOnline) {
    toast.error('Cannot download while offline');
    return { success: false, reason: 'offline' };
  }

  // Set initial state
  setIsDownloading?.(true);
  setDownloadSuccess?.(null);

  // Set up event listeners
  const downloadStartedListener = () => {
    console.log('Download started...');
  };

  const downloadProgressListener = progress => {
    console.log('Download progress:', progress);
    // You could update a progress bar here
  };

  const downloadCompletedListener = result => {
    console.log('Download completed:', result);
    setDownloadSuccess?.(true);
    setIsDownloading?.(false);

    if (result.success) {
      toast.success('Data downloaded successfully for offline use');
      // Emit global event
      globalEventBus.emit(GLOBAL_EVENTS.DOWNLOAD_COMPLETED, result);
    }

    // Remove event listeners
    cleanupListeners();
  };

  const downloadFailedListener = error => {
    console.error('Download failed:', error);
    setDownloadSuccess?.(false);
    setIsDownloading?.(false);
    toast.error(`Failed to download data: ${error.error || 'Unknown error'}`);

    // Remove event listeners
    cleanupListeners();
  };

  // Register event listeners
  downloadEvents.on(DOWNLOAD_EVENTS.STARTED, downloadStartedListener);
  downloadEvents.on(DOWNLOAD_EVENTS.PROGRESS, downloadProgressListener);
  downloadEvents.on(DOWNLOAD_EVENTS.COMPLETED, downloadCompletedListener);
  downloadEvents.on(DOWNLOAD_EVENTS.FAILED, downloadFailedListener);

  // Function to remove event listeners
  const cleanupListeners = () => {
    downloadEvents.off(DOWNLOAD_EVENTS.STARTED, downloadStartedListener);
    downloadEvents.off(DOWNLOAD_EVENTS.PROGRESS, downloadProgressListener);
    downloadEvents.off(DOWNLOAD_EVENTS.COMPLETED, downloadCompletedListener);
    downloadEvents.off(DOWNLOAD_EVENTS.FAILED, downloadFailedListener);
  };

  try {
    // Start the download process
    const result = await offlineDownloadService.downloadAllData(tenant);

    // Note: You don't need to handle success/failure here because
    // the event listeners above will handle it

    return result;
  } catch (error) {
    console.error('Unexpected error during download:', error);

    // This should only happen for truly unexpected errors
    // Most errors should be handled by the downloadFailedListener
    setDownloadSuccess?.(false);
    setIsDownloading?.(false);
    toast.error(`Unexpected error: ${error.message}`);

    // Clean up listeners
    cleanupListeners();

    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check for unsynced data in the database
 * @returns {Promise<boolean>} - Whether there's unsynced data
 */
export const checkUnsyncedData = async () => {
  try {
    const data = await offlineDBService.getUnsyncedData();
    const hasData =
      data.farmers?.length > 0 ||
      data.farms?.length > 0 ||
      data.forms?.length > 0;

    console.log('checkUnsyncedData result:', {
      hasData,
      farmerCount: data.farmers?.length || 0,
      farmCount: data.farms?.length || 0,
      formCount: data.forms?.length || 0
    });

    return hasData;
  } catch (error) {
    console.error('Error checking unsynced data:', error);
    return false;
  }
};

/**
 * Check if database is available
 * @returns {Promise<boolean>} - Whether database is available
 */
export const isDatabaseAvailable = async () => {
  try {
    const status = await offlineDBService.checkDatabaseStatus();
    return status.isOpen === true;
  } catch (error) {
    console.error('Error checking database status:', error);
    return false;
  }
};

// Export other utility functions as needed
export default {
  handleOfflineDownload,
  checkUnsyncedData,
  isDatabaseAvailable
};
